import type { AudioClip, Clip, FadeCurve, Project } from './types';
import { clipEndTicks, tempoMapOf } from './selectors';
import { secondsSpanToTicks, ticksToSeconds } from '@/timing/tempoMap';
import { headroomAfter, headroomBefore, recutSource } from './sourceSpan';

/**
 * Crossfades.
 *
 * A crossfade is not a new kind of object. It is the outgoing clip's fade-out
 * and the incoming clip's fade-in covering the same span of time, with
 * complementary curves. Everything downstream — the scheduler, the offline
 * renderer, the saved project, undo — already handles clip fades, so a
 * crossfade needs no engine changes at all and cannot drift out of step with
 * what is heard.
 *
 * The interesting part is geometric. Two clips butted end to end have no
 * overlap to fade across, so one or both must be extended into the other — and
 * that is only possible where there is unused source audio beyond the edit.
 * `planCrossfade` works out how much is available and returns the largest fade
 * that fits, which is what makes "crossfade this edit" do something sensible
 * rather than either failing or inventing silence.
 */

export interface CrossfadePlan {
  /** The earlier clip, extended if it had to be. */
  outgoing: AudioClip;
  /** The later clip, extended if it had to be. */
  incoming: AudioClip;
  /** Length of the fade, in seconds of timeline. */
  durationSec: number;
  /** True when the clips already overlapped and nothing had to be extended. */
  usedExistingOverlap: boolean;
}

export type CrossfadeFailure =
  | 'notAudio'
  | 'differentLanes'
  | 'notAdjacent'
  | 'noSourceAvailable'
  | 'sameClip';

export type CrossfadeResult = { ok: true; plan: CrossfadePlan } | { ok: false; reason: CrossfadeFailure };

/**
 * How far apart two clip edges may be and still count as the same edit.
 *
 * Positions are integers in ticks while lengths are seconds, so a clip's end
 * and its neighbour's start routinely differ by a fraction of a tick after a
 * split or a comp. A millisecond is far below anything audible and far above
 * that rounding, so it separates "these clips meet" from "these clips overlap"
 * without either being fooled by arithmetic.
 */
const EDGE_TOLERANCE_SEC = 0.001;

export { headroomAfter, headroomBefore } from './sourceSpan';

/**
 * Works out how to crossfade two clips, without changing the project.
 *
 * The requested length is met where the material allows and shortened where it
 * does not — a fade half as long as asked for is far more useful than a refusal,
 * and the caller is told what it actually got.
 */
export function planCrossfade(
  project: Project,
  first: Clip,
  second: Clip,
  requestedSec: number,
  curve: FadeCurve = 'equalPower',
): CrossfadeResult {
  if (first.id === second.id) return { ok: false, reason: 'sameClip' };
  if (first.kind !== 'audio' || second.kind !== 'audio') return { ok: false, reason: 'notAudio' };
  if (first.trackId !== second.trackId || first.takeLane !== second.takeLane) {
    return { ok: false, reason: 'differentLanes' };
  }

  // Order by position: which clip is outgoing is a fact about the timeline, not
  // about the order the caller happened to pass them in.
  const [outgoing, incoming] =
    first.startTicks <= second.startTicks ? [first, second] : [second, first];

  const map = tempoMapOf(project);
  const outgoingEnd = ticksToSeconds(map, clipEndTicks(project, outgoing));
  const incomingStart = ticksToSeconds(map, incoming.startTicks);
  const overlapSec = outgoingEnd - incomingStart;

  const requested = Math.max(0.001, requestedSec);

  if (overlapSec > EDGE_TOLERANCE_SEC) {
    // Already overlapping: fade across what is there, up to what was asked for
    // and never past either clip's own length.
    const durationSec = Math.min(overlapSec, requested, outgoing.durationSec, incoming.durationSec);
    if (durationSec <= 0) return { ok: false, reason: 'noSourceAvailable' };
    return {
      ok: true,
      plan: {
        outgoing: { ...outgoing, fadeOut: { durationSec, curve, curvature: outgoing.fadeOut.curvature } },
        incoming: { ...incoming, fadeIn: { durationSec, curve, curvature: incoming.fadeIn.curvature } },
        durationSec,
        usedExistingOverlap: true,
      },
    };
  }

  // A gap between the clips is not an edit to fade across; it is two separate
  // pieces of audio, and joining them would move material the user placed.
  const gapSec = -overlapSec;
  if (gapSec > EDGE_TOLERANCE_SEC) return { ok: false, reason: 'notAdjacent' };

  // Butted together. Extend each side by half the fade into its unused source.
  const wantEach = requested / 2;
  const afterOutgoing = Math.min(wantEach, headroomAfter(project, outgoing));
  const beforeIncoming = Math.min(wantEach, headroomBefore(project, incoming));
  const durationSec = afterOutgoing + beforeIncoming;
  if (durationSec <= 0.0005) return { ok: false, reason: 'noSourceAvailable' };

  // The outgoing clip grows later; the incoming one starts earlier, consuming
  // source it had skipped. Neither is resampled and neither loses material.
  // Both are stated in heard order, so a reversed clip reaches into the end of
  // its source it had not been playing rather than the start it already was.
  const grownOutgoing: AudioClip = {
    ...outgoing,
    ...recutSource(project, outgoing, 0, outgoing.durationSec + afterOutgoing),
    fadeOut: { durationSec, curve, curvature: outgoing.fadeOut.curvature },
  };

  const earlierStartTicks = Math.max(
    0,
    Math.round(incoming.startTicks - secondsSpanToTicks(map, incoming.startTicks, beforeIncoming)),
  );
  const grownIncoming: AudioClip = {
    ...incoming,
    startTicks: earlierStartTicks,
    ...recutSource(project, incoming, -beforeIncoming, incoming.durationSec + beforeIncoming),
    fadeIn: { durationSec, curve, curvature: incoming.fadeIn.curvature },
  };

  return {
    ok: true,
    plan: { outgoing: grownOutgoing, incoming: grownIncoming, durationSec, usedExistingOverlap: false },
  };
}

/**
 * Removes a crossfade and returns the join to a hard cut.
 *
 * Clearing only the fades would leave the two clips still overlapping, both
 * playing at full level — louder than either the crossfade or the original cut,
 * and not what "remove crossfade" means to anyone. So the overlap is trimmed
 * away too, back to the point at its centre, which is where the edit was before
 * the crossfade reached out from it.
 *
 * The trim is non-destructive like every other: it moves `offsetSec` and
 * `durationSec`, and the source audio is untouched, so crossfading again
 * afterwards has the same material to work with.
 */
export function clearCrossfade(
  project: Project,
  outgoing: AudioClip,
  incoming: AudioClip,
): { outgoing: AudioClip; incoming: AudioClip } {
  const map = tempoMapOf(project);
  const outgoingEndSec = ticksToSeconds(map, clipEndTicks(project, outgoing));
  const incomingStartSec = ticksToSeconds(map, incoming.startTicks);
  const overlapSec = outgoingEndSec - incomingStartSec;

  const plainOutgoing: AudioClip = { ...outgoing, fadeOut: { ...outgoing.fadeOut, durationSec: 0 } };
  const plainIncoming: AudioClip = { ...incoming, fadeIn: { ...incoming.fadeIn, durationSec: 0 } };
  if (overlapSec <= EDGE_TOLERANCE_SEC) return { outgoing: plainOutgoing, incoming: plainIncoming };

  const half = overlapSec / 2;
  const editTicks = Math.round(
    incoming.startTicks + secondsSpanToTicks(map, incoming.startTicks, half),
  );

  return {
    outgoing: { ...plainOutgoing, ...recutSource(project, outgoing, 0, outgoing.durationSec - half) },
    incoming: {
      ...plainIncoming,
      startTicks: Math.max(0, editTicks),
      ...recutSource(project, incoming, half, incoming.durationSec - half),
    },
  };
}

export interface CrossfadeRegion {
  outgoingId: string;
  incomingId: string;
  startTicks: number;
  endTicks: number;
}

/**
 * Finds the crossfades on a track lane: overlapping pairs whose facing fades
 * are both set. Used for drawing, and for finding the join nearest the
 * playhead when the user asks to crossfade without selecting anything.
 */
export function findCrossfades(project: Project, trackId: string, takeLane = 0): CrossfadeRegion[] {
  const track = project.tracks[trackId];
  if (!track) return [];

  const clips = track.clipIds
    .map((id) => project.clips[id])
    .filter((clip): clip is AudioClip => Boolean(clip) && clip.kind === 'audio' && clip.takeLane === takeLane)
    .sort((a, b) => a.startTicks - b.startTicks);

  const map = tempoMapOf(project);
  const regions: CrossfadeRegion[] = [];

  for (let i = 1; i < clips.length; i++) {
    const outgoing = clips[i - 1];
    const incoming = clips[i];
    const outgoingEnd = clipEndTicks(project, outgoing);
    if (outgoingEnd <= incoming.startTicks) continue;
    if (outgoing.fadeOut.durationSec <= 0 || incoming.fadeIn.durationSec <= 0) continue;

    const fadeTicks = secondsSpanToTicks(map, incoming.startTicks, incoming.fadeIn.durationSec);

    regions.push({
      outgoingId: outgoing.id,
      incomingId: incoming.id,
      startTicks: incoming.startTicks,
      endTicks: Math.min(outgoingEnd, incoming.startTicks + fadeTicks),
    });
  }

  return regions;
}

/**
 * The pair of clips forming the join nearest a position on a lane, if any.
 * "Nearest" is measured from the edit point, which is where the eye and the
 * playhead both are when someone asks for a crossfade.
 */
export function joinNearest(
  project: Project,
  trackId: string,
  tick: number,
  takeLane = 0,
): { outgoing: AudioClip; incoming: AudioClip } | null {
  const track = project.tracks[trackId];
  if (!track) return null;

  const clips = track.clipIds
    .map((id) => project.clips[id])
    .filter((clip): clip is AudioClip => Boolean(clip) && clip.kind === 'audio' && clip.takeLane === takeLane)
    .sort((a, b) => a.startTicks - b.startTicks);

  let best: { outgoing: AudioClip; incoming: AudioClip } | null = null;
  let bestDistance = Infinity;

  for (let i = 1; i < clips.length; i++) {
    const outgoing = clips[i - 1];
    const incoming = clips[i];
    const outgoingEnd = clipEndTicks(project, outgoing);
    // Only joins: overlapping or touching, never a pair with a gap between them.
    if (outgoingEnd + 1 < incoming.startTicks) continue;
    const distance = Math.abs(incoming.startTicks - tick);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = { outgoing, incoming };
    }
  }

  return best;
}
